import React, { useCallback, useEffect, useState, useRef, memo } from 'react';
import { fetchOHLC, OHLCData } from '../../utils/krakenAPI';
interface TradingViewChartProps {
  symbol?: string;
  theme?: 'light' | 'dark';
  interval?: string;
  height?: number | string;
  width?: number | string;
}
const intervalMap: Record<string, number> = {
  '1': 1,
  '5': 5,
  '15': 15,
  '30': 30,
  '60': 60,
  '240': 240,
  '1D': 1440,
  D: 1440
};
function symbolToKrakenPair(symbol: string): string {
  const map: Record<string, string> = {
    'BITSTAMP:BTCUSD': 'XBTUSD',
    BTCUSD: 'XBTUSD',
    ETHUSD: 'XETHZUSD',
    SOLUSD: 'SOLUSD'
  };
  return map[symbol] || 'XBTUSD';
}
// Colors
const BG = '#1A1A2E';
const GRID = 'rgba(255,255,255,0.04)';
const TEXT = '#8B8B9E';
const BORDER = '#2A2A3E';
const UP = '#00C87F';
const DOWN = '#E85D75';
const UP_VOL = 'rgba(0,200,127,0.3)';
const DOWN_VOL = 'rgba(232,93,117,0.3)';
const CROSSHAIR = '#7A2EFF';
function formatPrice(p: number): string {
  if (p >= 10000) return p.toFixed(0);
  if (p >= 100) return p.toFixed(1);
  if (p >= 1) return p.toFixed(2);
  return p.toFixed(4);
}
function formatTime(ts: number, intervalMin: number): string {
  const d = new Date(ts);
  if (intervalMin >= 1440) {
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  }
  const h = d.getUTCHours().toString().padStart(2, '0');
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  if (intervalMin >= 60) return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${h}:${m}`;
  return `${h}:${m}`;
}
function drawChart(canvas: HTMLCanvasElement, data: OHLCData[], intervalMin: number, mouse: {
  x: number;
  y: number;
} | null, startIdx: number, endIdx: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx || data.length === 0) return;
  const visibleData = data.slice(startIdx, endIdx);
  if (visibleData.length === 0) return;
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth;
  const H = canvas.clientHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);
  // Layout
  const priceAxisW = 70;
  const timeAxisH = 28;
  const chartW = W - priceAxisW;
  const volumeH = (H - timeAxisH) * 0.15;
  const candleH = H - timeAxisH - volumeH;
  const padding = 4;
  // Clear
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  // Price range
  const highs = visibleData.map(d => d.high);
  const lows = visibleData.map(d => d.low);
  const maxPrice = Math.max(...highs);
  const minPrice = Math.min(...lows);
  const priceRange = maxPrice - minPrice || 1;
  const pricePad = priceRange * 0.08;
  const maxVol = Math.max(...visibleData.map(d => d.volume)) || 1;
  const toY = (price: number) => {
    return padding + (candleH - 2 * padding) * (1 - (price - (minPrice - pricePad)) / (priceRange + 2 * pricePad));
  };
  const toVolY = (vol: number) => {
    return candleH + volumeH * (1 - vol / maxVol);
  };
  const barW = Math.max(1, (chartW - 10) / visibleData.length);
  const candleW = Math.max(1, barW * 0.6);
  // Grid lines (horizontal)
  const gridLines = 5;
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  ctx.font = '10px Inter, sans-serif';
  ctx.fillStyle = TEXT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= gridLines; i++) {
    const price = minPrice - pricePad + (priceRange + 2 * pricePad) * (1 - i / gridLines);
    const y = Math.round(toY(price)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(chartW, y);
    ctx.stroke();
    ctx.fillText(formatPrice(price), W - 6, y);
  }
  // Border between chart and price axis
  ctx.strokeStyle = BORDER;
  ctx.beginPath();
  ctx.moveTo(chartW + 0.5, 0);
  ctx.lineTo(chartW + 0.5, H - timeAxisH);
  ctx.stroke();
  // Border between chart and time axis
  ctx.beginPath();
  ctx.moveTo(0, H - timeAxisH + 0.5);
  ctx.lineTo(W, H - timeAxisH + 0.5);
  ctx.stroke();
  // Volume separator
  ctx.strokeStyle = GRID;
  ctx.beginPath();
  ctx.moveTo(0, candleH + 0.5);
  ctx.lineTo(chartW, candleH + 0.5);
  ctx.stroke();
  // Draw candles and volume
  for (let i = 0; i < visibleData.length; i++) {
    const d = visibleData[i];
    const x = 5 + i * barW + barW / 2;
    const isUp = d.close >= d.open;
    const color = isUp ? UP : DOWN;
    // Volume bar
    const volTop = toVolY(d.volume);
    ctx.fillStyle = isUp ? UP_VOL : DOWN_VOL;
    ctx.fillRect(x - candleW / 2, volTop, candleW, candleH + volumeH - volTop);
    // Wick
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(x) + 0.5, toY(d.high));
    ctx.lineTo(Math.round(x) + 0.5, toY(d.low));
    ctx.stroke();
    // Body
    const bodyTop = toY(Math.max(d.open, d.close));
    const bodyBot = toY(Math.min(d.open, d.close));
    const bodyH = Math.max(1, bodyBot - bodyTop);
    ctx.fillStyle = color;
    ctx.fillRect(x - candleW / 2, bodyTop, candleW, bodyH);
  }
  // Draw 20-period SMA
  const smaPeriod = 20;
  if (visibleData.length >= smaPeriod) {
    ctx.strokeStyle = '#7A2EFF';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    let started = false;
    for (let i = smaPeriod - 1; i < visibleData.length; i++) {
      let sum = 0;
      for (let j = i - smaPeriod + 1; j <= i; j++) {
        sum += visibleData[j].close;
      }
      const avg = sum / smaPeriod;
      const x = 5 + i * barW + barW / 2;
      const y = toY(avg);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }
  // Time labels
  ctx.fillStyle = TEXT;
  ctx.font = '10px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const labelInterval = Math.max(1, Math.floor(visibleData.length / 6));
  for (let i = 0; i < visibleData.length; i += labelInterval) {
    const x = 5 + i * barW + barW / 2;
    if (x < chartW - 30) {
      ctx.fillText(formatTime(visibleData[i].time, intervalMin), x, H - timeAxisH + 8);
    }
  }
  // Crosshair
  if (mouse && mouse.x > 0 && mouse.x < chartW && mouse.y > 0 && mouse.y < H - timeAxisH) {
    ctx.strokeStyle = CROSSHAIR;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    // Vertical
    ctx.beginPath();
    ctx.moveTo(Math.round(mouse.x) + 0.5, 0);
    ctx.lineTo(Math.round(mouse.x) + 0.5, H - timeAxisH);
    ctx.stroke();
    // Horizontal
    ctx.beginPath();
    ctx.moveTo(0, Math.round(mouse.y) + 0.5);
    ctx.lineTo(chartW, Math.round(mouse.y) + 0.5);
    ctx.stroke();
    ctx.setLineDash([]);
    // Price label on right axis
    const hoverPrice = minPrice - pricePad + (priceRange + 2 * pricePad) * (1 - (mouse.y - padding) / (candleH - 2 * padding));
    if (mouse.y < candleH) {
      ctx.fillStyle = CROSSHAIR;
      ctx.fillRect(chartW, mouse.y - 10, priceAxisW, 20);
      ctx.fillStyle = '#fff';
      ctx.font = '10px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(formatPrice(hoverPrice), W - 6, mouse.y);
    }
    // Find nearest candle for tooltip
    const idx = Math.min(visibleData.length - 1, Math.max(0, Math.floor((mouse.x - 5) / barW)));
    const d = visibleData[idx];
    if (d) {
      const isUp = d.close >= d.open;
      const tooltipW = 160;
      const tooltipH = 90;
      let tx = mouse.x + 12;
      let ty = mouse.y - tooltipH / 2;
      if (tx + tooltipW > chartW) tx = mouse.x - tooltipW - 12;
      if (ty < 0) ty = 4;
      if (ty + tooltipH > candleH) ty = candleH - tooltipH - 4;
      // Tooltip background
      ctx.fillStyle = 'rgba(20,22,32,0.9)';
      ctx.beginPath();
      ctx.roundRect(tx, ty, tooltipW, tooltipH, 8);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.font = '10px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const lines = [{
        label: 'Time',
        value: formatTime(d.time, intervalMin)
      }, {
        label: 'O',
        value: formatPrice(d.open),
        color: TEXT
      }, {
        label: 'H',
        value: formatPrice(d.high),
        color: UP
      }, {
        label: 'L',
        value: formatPrice(d.low),
        color: DOWN
      }, {
        label: 'C',
        value: formatPrice(d.close),
        color: isUp ? UP : DOWN
      }];
      lines.forEach((line, li) => {
        const ly = ty + 8 + li * 16;
        ctx.fillStyle = '#6B6B7E';
        ctx.fillText(line.label, tx + 8, ly);
        ctx.fillStyle = line.color || TEXT;
        ctx.textAlign = 'right';
        ctx.fillText(line.value, tx + tooltipW - 8, ly);
        ctx.textAlign = 'left';
      });
    }
  }
}
export const TradingViewChart = memo(function TradingViewChart({
  symbol = 'BITSTAMP:BTCUSD',
  theme = 'dark',
  interval = '15',
  height = '100%',
  width = '100%'
}: TradingViewChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<OHLCData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mouseRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const rafRef = useRef<number>(0);
  const viewportRef = useRef({
    startIdx: 0,
    endIdx: 0
  });
  const targetViewportRef = useRef({
    startIdx: 0,
    endIdx: 0
  });
  const animFrameRef = useRef<number>(0);
  const dragRef = useRef<{
    startX: number;
    startIdx: number;
  } | null>(null);
  const krakenPair = symbolToKrakenPair(symbol);
  const krakenInterval = intervalMap[interval] || 15;
  const redraw = useCallback(() => {
    if (canvasRef.current && data.length > 0) {
      const {
        startIdx,
        endIdx
      } = viewportRef.current;
      drawChart(canvasRef.current, data, krakenInterval, mouseRef.current, startIdx, endIdx);
    }
  }, [data, krakenInterval]);
  // Lerp animation loop for smooth zoom transitions
  const animateViewport = useCallback(() => {
    const current = viewportRef.current;
    const target = targetViewportRef.current;
    const diffStart = target.startIdx - current.startIdx;
    const diffEnd = target.endIdx - current.endIdx;
    if (Math.abs(diffStart) < 0.5 && Math.abs(diffEnd) < 0.5) {
      viewportRef.current = {
        startIdx: Math.round(target.startIdx),
        endIdx: Math.round(target.endIdx)
      };
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
      return;
    }
    const lerpFactor = 0.15;
    viewportRef.current = {
      startIdx: current.startIdx + diffStart * lerpFactor,
      endIdx: current.endIdx + diffEnd * lerpFactor
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(redraw);
    animFrameRef.current = requestAnimationFrame(animateViewport);
  }, [redraw]);
  // Fetch data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOHLC(krakenPair, krakenInterval).then(d => {
      if (!cancelled) {
        setData(d);
        const len = d.length;
        const vp = {
          startIdx: Math.max(0, len - 80),
          endIdx: len
        };
        viewportRef.current = vp;
        targetViewportRef.current = {
          ...vp
        };
        setLoading(false);
      }
    }).catch(err => {
      if (!cancelled) {
        setError('Failed to load chart data');
        setLoading(false);
      }
    });
    const timer = setInterval(() => {
      fetchOHLC(krakenPair, krakenInterval).then(d => {
        if (!cancelled) {
          setData(d);
          // Keep viewport at end if user was already at the end
          const vp = viewportRef.current;
          if (vp.endIdx >= d.length - 2) {
            const visible = vp.endIdx - vp.startIdx;
            const newVp = {
              startIdx: Math.max(0, d.length - visible),
              endIdx: d.length
            };
            viewportRef.current = newVp;
            targetViewportRef.current = {
              ...newVp
            };
          }
        }
      }).catch(() => {});
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [krakenPair, krakenInterval]);
  // Draw on data change or resize
  useEffect(() => {
    redraw();
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(container);
    return () => ro.disconnect();
  }, [redraw]);
  // Mouse tracking + zoom + pan
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
    };
    const onLeave = () => {
      mouseRef.current = null;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (data.length === 0) return;
      const {
        startIdx,
        endIdx
      } = targetViewportRef.current;
      const visible = endIdx - startIdx;
      const delta = e.deltaY > 0 ? Math.ceil(visible * 0.04) : -Math.ceil(visible * 0.04);
      const newVisible = Math.max(20, Math.min(data.length, visible + delta));
      const rect = canvas.getBoundingClientRect();
      const mouseRatio = (e.clientX - rect.left) / rect.width;
      const newStart = Math.max(0, Math.round(endIdx - newVisible + (visible - newVisible) * mouseRatio));
      const newEnd = Math.min(data.length, newStart + newVisible);
      targetViewportRef.current = {
        startIdx: Math.max(0, newEnd - newVisible),
        endIdx: newEnd
      };
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = requestAnimationFrame(animateViewport);
    };
    const onDown = (e: MouseEvent) => {
      dragRef.current = {
        startX: e.clientX,
        startIdx: viewportRef.current.startIdx
      };
      canvas.style.cursor = 'grabbing';
    };
    const onDrag = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const visible = viewportRef.current.endIdx - viewportRef.current.startIdx;
      const barW = canvas.clientWidth / visible;
      const shift = Math.round(-dx / (barW * 1.5));
      const newStart = Math.max(0, Math.min(data.length - visible, dragRef.current.startIdx + shift));
      viewportRef.current = {
        startIdx: newStart,
        endIdx: newStart + visible
      };
      targetViewportRef.current = {
        startIdx: newStart,
        endIdx: newStart + visible
      };
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
    };
    const onUp = () => {
      dragRef.current = null;
      canvas.style.cursor = 'crosshair';
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('wheel', onWheel, {
      passive: false
    });
    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', onUp);
    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onUp);
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [redraw, animateViewport, data.length]);
  return <div ref={containerRef} style={{
    height: typeof height === 'number' ? `${height}px` : height,
    width: typeof width === 'number' ? `${width}px` : width,
    borderRadius: 'inherit',
    overflow: 'hidden',
    background: BG,
    position: 'relative'
  }} data-id="element-2331">
      {loading && <div className="absolute inset-0 flex items-center justify-center z-10" data-id="element-2332">
          <div className="flex items-center gap-2 text-text-secondary text-sm" data-id="element-2333">
            <div className="w-4 h-4 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" data-id="element-2334" />
            Loading chart…
          </div>
        </div>}
      {error && !loading && <div className="absolute inset-0 flex items-center justify-center z-10" data-id="element-2335">
          <div className="text-danger text-sm" data-id="element-2336">{error}</div>
        </div>}
      <canvas ref={canvasRef} style={{
      width: '100%',
      height: '100%',
      display: 'block',
      cursor: 'crosshair'
    }} data-id="element-2337" />
    </div>;
});